// components/CustomScriptInjector/hooks.ts — Script injection effect
import { useEffect, useRef } from 'react';
import type { Form } from '../../types';
import { validateDomain } from './domains';
import { extractScripts } from './extract';
import { collectScripts } from './collect';
import { waitForContentReady } from '../../utils/pixelGate';

export function useCustomScriptInjection(form?: Form, location?: 'formPage' | 'thankYouPage', scriptContent?: string, brandId?: string, dedupeKey?: string): void {
    const injectedElements = useRef<HTMLElement[]>([]);

    useEffect(() => {
        // Cleanup previous scripts if content changes
        injectedElements.current.forEach(el => {
            if (document.head.contains(el)) {
                document.head.removeChild(el);
            }
        });
        injectedElements.current = [];

        // Collect all scripts to inject
        const scriptsToInjectList = collectScripts(form, location, scriptContent, dedupeKey);

        if (scriptsToInjectList.length === 0) {
            return;
        }

        const effectiveBrandId = brandId ?? form?.brandId ?? undefined;
        // GATE sukses-load (speed 2026-10-22, pola pixelGate): snippet warisan
        // form (Google Tag base 165KB + destination 191KB) JANGAN berebut
        // bandwidth/CPU dengan LCP. Inject setelah load+LCP (maks 5 dtk).
        // Event transaksional (klik CTA/Purchase) tetap bypass — hook lain.
        let cancelled = false;

        const injectAll = async () => {
            // Anchor FIXED: semua script disisipkan sebelum node yang sama supaya
            // urutan eksekusi = urutan di scriptsToInjectList. Sebelumnya
            // `document.head.firstChild` dibaca ulang tiap iterasi → urutan TERBALIK
            // → snippet event Google jalan sebelum <script> base tag-nya (gtag undefined).
            const anchor = document.head.firstChild;
            for (const content of scriptsToInjectList) {
                const scriptsToInject = extractScripts(content);

                for (const scriptData of scriptsToInject) {
                    const scriptEl = document.createElement('script');
                    scriptEl.type = 'text/javascript';
                    scriptEl.id = `custom-script-${location}-${injectedElements.current.length}`;

                    scriptEl.onerror = (e) => {
                        console.warn(`[CustomScript] External script error (${location}):`, e);
                    };

                    if (scriptData.src) {
                        const isValid = await validateDomain(scriptData.src, effectiveBrandId);
                        if (!isValid) {
                            console.warn(`[CustomScript] Skipped external script (not whitelisted): ${scriptData.src}`);
                            continue;
                        }
                        scriptEl.src = scriptData.src;
                        scriptEl.async = true;
                    }

                    if (scriptData.code) {
                        scriptEl.textContent = scriptData.code;
                    }

                    document.head.insertBefore(scriptEl, anchor);
                    injectedElements.current.push(scriptEl);
                }
            }

            console.log(`[CustomScript] Injected ${injectedElements.current.length} script(s) for ${location || 'landing page'}`);
        };

        void waitForContentReady().then(() => {
            if (cancelled) return;
            injectAll().catch(error => {
                console.error(`[CustomScript] Error injecting custom scripts for ${location || 'landing page'}:`, error);
            });
        });

        // Cleanup on unmount
        return () => {
            cancelled = true;
            injectedElements.current.forEach(el => {
                if (document.head.contains(el)) {
                    document.head.removeChild(el);
                }
            });
            injectedElements.current = [];
        };

    }, [form?.customScripts, location, scriptContent, brandId, dedupeKey]);
}
